import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Import animations
import Animation from './Animation';

// Import CSS
import './variables.css';
import './layout.css';
import './components.css';
import './pages.css';
import './mobile-optimizations.css';
import './modern-nav.css';
import './mazra-enhancements.css';

// New brand logo path
const newBrandLogo = '/baloch.jpg';

// Import Components with lazy loading
const Home = lazy(() => import('./Homes'));
const About = lazy(() => import('./Abouts'));
const Contact = lazy(() => import('./Contacts'));

const { useTouchOptimizer } = Animation;

// Loading Component
const LoadingSpinner = () => (
  <div className="initial-loading">
    <div className="spinner"></div>
  </div>
);

// ScrollToTop component to scroll to top on page change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);
  
  return null;
};

// ScrollToTopButton component
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  
  return (
    <motion.div 
      className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
      onClick={scrollToTop}
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      aria-label="Scroll to top"
    >
      <i className="fas fa-arrow-up"></i>
    </motion.div>
  );
};

// PageTransition component
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {children}
    </motion.div>
  );
};

// Main App Component
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  
  // Use touch optimizer
  useTouchOptimizer();
  
  useEffect(() => {
    // Simulate loading time for smooth transitions
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    
    // Add scroll event listener for header styling with performance optimization
    const handleScroll = () => {
      const scrolled = window.scrollY > 30;
      if (scrolled !== isHeaderScrolled) {
        setIsHeaderScrolled(scrolled);
      }
    };

    // Throttled scroll event for better performance
    let scrollTimeout;
    const throttledScroll = () => {
      if (!scrollTimeout) {
        scrollTimeout = setTimeout(() => {
          handleScroll();
          scrollTimeout = null;
        }, 100);
      }
    };

    window.addEventListener('scroll', throttledScroll);
    
    // Load font awesome asynchronously to improve performance
    const loadFontAwesome = async () => {
      if (!document.querySelector('[data-fa-load]')) {
        const fontAwesome = document.createElement('link');
        fontAwesome.rel = 'stylesheet';
        fontAwesome.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css';
        fontAwesome.setAttribute('data-fa-load', 'true');
        document.head.appendChild(fontAwesome);
      }
    };
    
    // Load fonts async
    loadFontAwesome();
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [isHeaderScrolled]);
  
  // We'll use the new isLoading state to show a loading spinner
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  return (
    <Router>
      <ScrollToTop />
      <div className="app">
        <motion.header
          className={isHeaderScrolled ? 'scrolled' : ''}
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 clamp(1.5rem, 5vw, 3.5rem)',
            backgroundColor: '#001f3f',
            boxShadow: isHeaderScrolled ? '0 2px 12px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.2)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            height: isHeaderScrolled ? '65px' : '75px',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${isHeaderScrolled ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'}`,
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <motion.div
            className="logo"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 1001
            }}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Link to="/" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              textDecoration: 'none'
            }}>
              <img
                src={newBrandLogo}
                alt="Bholat's Kitchen"
                style={{
                  height: isHeaderScrolled ? '40px' : '48px',
                  width: 'auto',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '6px'
                }}
                loading="eager"
              />
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}>
                <span style={{
                  fontFamily: 'var(--font-brand)',
                  fontSize: isHeaderScrolled ? '1.2rem' : '1.4rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  letterSpacing: '0.05em',
                  lineHeight: 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textTransform: 'uppercase'
                }}>
                  BHOLAT'S
                </span>
                <span style={{
                  fontFamily: 'var(--font-brand)',
                  fontSize: isHeaderScrolled ? '0.55rem' : '0.65rem',
                  fontWeight: 400,
                  color: '#ffffff',
                  letterSpacing: '0.35em',
                  lineHeight: 1.3,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textTransform: 'uppercase'
                }}>
                  KITCHEN
                </span>
              </div>
            </Link>
          </motion.div>

          <div className="nav-links">
            <nav>
              <ul style={{
                display: 'flex',
                listStyle: 'none',
                gap: 'clamp(1rem, 3vw, 2.5rem)',
                margin: 0,
                padding: 0,
                alignItems: 'center'
              }}>
                {[
                  { path: '/', label: 'Home' },
                  { path: '/about', label: 'About' },
                  { path: '/contact', label: 'Contact' }
                ].map((item, index) => (
                  <motion.li
                    key={item.path}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <Link
                      to={item.path}
                      style={{
                        fontWeight: 600,
                        fontSize: 'clamp(0.875rem, 1.5vw, 0.95rem)',
                        letterSpacing: '0.5px',
                        color: '#ffffff',
                        textDecoration: 'none',
                        padding: '0.5rem 0.875rem',
                        position: 'relative',
                        display: 'inline-block',
                        transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        textTransform: 'uppercase'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#3e7bfa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#ffffff';
                      }}
                    >
                      {item.label}
                      <span style={{
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        width: 0,
                        height: '2px',
                        background: 'var(--accent-color)',
                        transform: 'translateX(-50%)',
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '2px'
                      }}></span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </nav>
          </div>
        </motion.header>
        
        <main style={{ 
          marginTop: '80px',
          paddingBottom: '2rem'
        }}>
          <Suspense fallback={<LoadingSpinner />}>
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={
                  <PageTransition>
                    <Home />
                  </PageTransition>
                } />
                <Route path="/about" element={
                  <PageTransition>
                    <About />
                  </PageTransition>
                } />
                <Route path="/contact" element={
                  <PageTransition>
                    <Contact />
                  </PageTransition>
                } />
              </Routes>
            </AnimatePresence>
          </Suspense>
        </main>
        <ScrollToTopButton />

        {/* Footer */}
        <footer style={{
          background: 'var(--bg-dark)',
          color: 'var(--text-inverse)',
          padding: '3rem 0 1.5rem',
          marginTop: 'auto'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 clamp(1rem, 3vw, 2rem)',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <img
                  src={newBrandLogo}
                  alt="Bholat's Kitchen"
                  style={{
                    height: '50px',
                    width: 'auto',
                    borderRadius: '6px'
                  }}
                />
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}>
                  <span style={{
                    fontFamily: 'var(--font-brand)',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: 'white',
                    letterSpacing: '0.05em',
                    lineHeight: 1,
                    textTransform: 'uppercase'
                  }}>
                    BHOLAT'S
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-brand)',
                    fontSize: '0.7rem',
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.8)',
                    letterSpacing: '0.35em',
                    lineHeight: 1.3,
                    textTransform: 'uppercase'
                  }}>
                    KITCHEN
                  </span>
                </div>
              </div>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.95rem',
                maxWidth: '400px',
                lineHeight: 1.6
              }}>
                Bold Desi flavors meet homemade comfort. 100% Zabiah Halal Certified.
              </p>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1.5rem',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              <Link to="/" style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = 'white'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
              >
                Home
              </Link>
              <Link to="/about" style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = 'white'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
              >
                About
              </Link>
              <Link to="/contact" style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = 'white'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.8)'}
              >
                Contact
              </Link>
            </div>

            <div style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              paddingTop: '1.5rem'
            }}>
              <p style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.85rem',
                margin: 0
              }}>
                © {new Date().getFullYear()} Bholat's Kitchen. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;